import React from 'react';
import isEmpty from 'lodash/isEmpty';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import * as actionCreators from 'actions/conversation';
import { fetchBookingsRequest } from 'actions/index';
import { bookingsData } from 'selectors/bookings';
import { userData, isCompany as isCompanySelector } from 'selectors/user';
import Main from 'components/templates/Main';
import Success from 'components/shared/Success';
import { isDeletedUser } from '../conversationLock';
import OffersTab from '../components/OffersTab';
import ConversationTabs from '../components/ConversationTabs';
import ShowMoreButton from '../components/ShowMoreButton';

const ViewBooking = () => {
  const intl = useIntl();
  const { conversationId } = useParams();
  const dispatch = useDispatch();
  const currentUser = useSelector(userData);
  const isCompany = useSelector(isCompanySelector);
  const bookings = useSelector(bookingsData);

  const { isFetching, error, success, isPosting, data } = useSelector(
    (state) => state.conversation,
  );

  React.useEffect(() => {
    if (!isFetching) {
      dispatch(fetchBookingsRequest({ conversationId }));
    }

    if (!isFetching && isEmpty(data)) {
      dispatch(actionCreators.fetchConversationRequest({ conversationId }));
    }

    return () => {
      dispatch(actionCreators.clearConversation());
    };
  }, []);

  // A failed fetch (missing conversation / no access) sets `error` and leaves
  // `data` empty. Stop the loader on error and surface a message in OffersTab
  // instead of spinning forever or exposing the booking-offer tab.
  const hasError = !isEmpty(error);
  const hasData = !isEmpty(data);
  const isLoading = isFetching || isEmpty(currentUser) || (!hasData && !hasError);
  const showLoader = isLoading;
  const loaderProps = { showLoader, hasError, isPosting };
  const isHost = currentUser.has_reviewed_cottages;

  let displayName = '';
  let otherUsers = [];

  // Only when the conversation actually loaded -- on error `data` is still empty.
  if (hasData) {
    otherUsers = (Array.isArray(data.Users) ? data.Users : []).filter(
      (user) => user.id !== currentUser.id,
    );
    displayName = otherUsers
      .map((user) =>
        isDeletedUser(user)
          ? intl.formatMessage({ id: 'DeletedUser', defaultMessage: 'Deleted user' })
          : user.name,
      )
      .join(', ');
  }

  return (
    <Main
      title={displayName}
      extra={
        data && data?.filters?.commission ? null : (
          <ShowMoreButton data={data} isFetching={isFetching} currentUser={currentUser} />
        )
      }
    >
      {hasData && <ConversationTabs tab={0} />}
      <OffersTab
        {...loaderProps}
        {...{
          bookings,
          conversationId,
          isHost,
          isCompany,
          locked: Boolean(data?.lock_reason),
        }}
      />
      <Success success={success} onClose={() => dispatch(actionCreators.clearConversation())} />
    </Main>
  );
};

export default ViewBooking;
